/*
<purpose>
  Standalone Von/Bis date-range picker for use in FilterPanel and any future
  consumer. Emits ISO yyyy-mm-dd strings (or null when cleared) via onChange.
  Designed to slot into FilterPanel's existing row without resizing surrounding
  elements — each input lives in its own labelled block.
</purpose>
<dependencies>React, theme tokens</dependencies>
<constraints>
  Does NOT wire itself into FilterPanel — that coupling belongs to Task 5 where
  ArchivSeite migrates to server-side from/to. This component is purely presentational.
</constraints>
*/
import React from 'react';
import { theme } from '../theme';

export interface DateRangeValue {
  from: string | null;
  to: string | null;
}

export interface DateRangeInputProps {
  from: string | null;
  to: string | null;
  onChange: (range: DateRangeValue) => void;
  /** Optional section label rendered above the Von/Bis pair (e.g. "Zeitraum"). */
  label?: string;
}

const labelStyle: React.CSSProperties = {
  fontSize: 11,
  color: theme.colors.textSecondary,
  display: 'block',
  marginBottom: 6,
  fontFamily: theme.font.family,
};

const inputStyle: React.CSSProperties = {
  width: '100%',
  boxSizing: 'border-box',
  padding: `${theme.spacing.xs}px ${theme.spacing.sm}px`,
  border: `1px solid ${theme.colors.borderLight}`,
  borderRadius: theme.borderRadius.sm,
  fontSize: 13,
  fontFamily: theme.font.family,
  outline: 'none',
  backgroundColor: theme.colors.white,
};

export const DateRangeInput: React.FC<DateRangeInputProps> = ({ from, to, onChange, label }) => {
  const handleVonChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    onChange({ from: val === '' ? null : val, to });
  };

  const handleBisChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    onChange({ from, to: val === '' ? null : val });
  };

  return (
    <div>
      {label && <span style={labelStyle}>{label}</span>}
      <div style={{ display: 'flex', gap: theme.spacing.sm, alignItems: 'flex-end' }}>
        <div style={{ flex: 1 }}>
          <label htmlFor="date-range-von" style={labelStyle}>Von</label>
          <input
            id="date-range-von"
            type="date"
            value={from ?? ''}
            onChange={handleVonChange}
            style={inputStyle}
          />
        </div>
        <div style={{ flex: 1 }}>
          <label htmlFor="date-range-bis" style={labelStyle}>Bis</label>
          <input
            id="date-range-bis"
            type="date"
            value={to ?? ''}
            onChange={handleBisChange}
            style={inputStyle}
          />
        </div>
      </div>
    </div>
  );
};
